import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import FormContentManager from "../../../components/common/form/FormContentManager";
import TableCell from "../../../components/common/ui/TableCell";
import DesignTreePicker from "../../../components/design/DesignTreePicker";

import { getMaterialDoc } from "../../../api/material/meta";
import PeopleCellLoader from "../../../components/common/people/PeopleCellLoader";

export default function Plan_new() {
  const { id: projectId, dns: paramDns } = useParams();

  const [dns, setDns] = useState("");
  const [openPicker, setOpenPicker] = useState(false);
  const [reviewer, setReviewer] = useState([]);

  const [form, setForm] = useState({
    title: "",
    related_document: "",
    relatedDrawing: null,
    division: "",
    area: "",
    level: "",
    grid: "",
    request_date: "",
    action_date: "",
    description: "",
  });

  const safeForm = form;

  /* =====================================================
     기존 문서 로딩
  ===================================================== */
  useEffect(() => {
    if (!paramDns) return;

    (async () => {
      try {
        const res = await getMaterialDoc({
          dns: paramDns,
          project: projectId,
        });

        console.log("📄 Plan_new 문서 로딩:", res);

        const header = res?.header || {};
        const isRejected = res?.status === "rejected";

        // ⭐ 반려된 문서인 경우 dns를 설정하지 않음 (새 문서로 생성)
        // 일반 문서인 경우에만 dns를 설정 (기존 문서 수정)
        if (!isRejected) {
          setDns(paramDns);
        } else {
          console.log("🔄 반려 문서 복사 모드: 새 문서로 생성됩니다");
        }

        setForm({
          title: res.title || "",
          related_document: header.related_document || "",
          relatedDrawing: header.relatedDrawing || null,
          division: header.division || "",
          area: header.area || "",
          level: header.level || "",
          grid: header.grid || "",
          request_date: header.request_date || "",
          action_date: header.action_date || "",
          description: header.description || "",
        });

        // CM 정보 로드 (반려되지 않은 경우에만)
        if (!isRejected && res?.reviewer) {
          setReviewer(
            Array.isArray(res.reviewer) ? res.reviewer : [res.reviewer]
          );
        } else if (isRejected) {
          // 반려된 문서는 CM 정보 초기화 (새로 지정해야 함)
          setReviewer([]);
        }
      } catch (e) {
        console.error("문서 로딩 에러:", e);
        alert(`문서를 불러오지 못했습니다: ${e.message}`);
      }
    })();
  }, [paramDns, projectId]);

  /* =====================================================
     입력 핸들러
  ===================================================== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const updateForm = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
  };

  const divisionOptions = [
    "건축",
    "구조",
    "토목",
    "설비",
    "전기",
    "소방",
    "의장",
    "기타",
  ];

  /* =====================================================
     🔥 SUMMARY CONFIG (유지)
  ===================================================== */
  const summaryConfig = {
    title: "자재 승인 요청 요약",
    sections: [
      { label: "문서명", value: safeForm.title || "-" },
      { label: "공종", value: safeForm.division || "-" },
      { label: "위치", value: safeForm.area || "-" },
      { label: "층", value: safeForm.level || "-" },
      { label: "좌표", value: safeForm.grid || "-" },
      { label: "요청일", value: safeForm.request_date || "-" },
      { label: "시행일", value: safeForm.action_date || "-" },
    ],
    list: [],
  };

  /* =====================================================
     🔥 material PS 저장 body (정석)
  ===================================================== */
  const getBody = useCallback(() => {
    return {
      values: {
        ...(dns ? { dns } : {}),

        project: projectId,
        module: "material",
        doc_type: "PS",

        title: safeForm.title?.trim() || "제목 없음",

        header: {
          related_document: safeForm.related_document || null,
          relatedDrawing: safeForm.relatedDrawing,
          division: safeForm.division || null,
          area: safeForm.area || null,
          level: safeForm.level || null,
          grid: safeForm.grid || null,
          request_date: safeForm.request_date || null,
          action_date: safeForm.action_date || null,
          description: safeForm.description || "",
        },

        lines: [], // ✅ PS는 항상 빈 배열
        level2: "GEN",
      },
    };
  }, [safeForm, projectId, dns]);

  /* =====================================================
     RENDER
  ===================================================== */
  return (
    <FormContentManager
      params={{
        module: "material",
        project: projectId,
        dns,
      }}
      getBody={getBody}
      reviewer={reviewer}
      onChangeId={setDns}
      redirectTo={`/project/${projectId}/material/materialboard`}
      docName="자재 승인 요청서"
      docDescribe="작업 계획을 아래 양식에 따라 작성하세요."
      summaryConfig={summaryConfig}
    >
      {/* 반려 문서 복사 안내 */}
      {paramDns && !dns && (
        <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
          <h3 className="text-sm font-bold text-blue-800 mb-1">
            📋 반려 문서 복사 모드
          </h3>
          <p className="text-xs text-blue-700">
            반려된 원본 문서는 그대로 유지되며, 수정 후 저장하면{" "}
            <strong>새로운 문서</strong>로 생성됩니다.
          </p>
          <p className="text-xs text-blue-600 mt-1">
            새 문서는 초안 상태로 저장되며, 결재선이 초기화됩니다.
          </p>
        </div>
      )}

      <div className="bg-white">
        <table className="doc-table w-full">
          <tbody>
            {/* 문서 제목 */}
            <tr>
              <TableCell
                label="문서 제목"
                colSpan={3}
                editing
                input={
                  <input
                    name="title"
                    value={safeForm.title}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                  />
                }
              />
            </tr>

            {/* CM */}
            <tr>
              <TableCell
                label="CM"
                colSpan={3}
                editing
                input={
                  <PeopleCellLoader
                    single
                    dns={paramDns}
                    value={reviewer}
                    onChange={setReviewer}
                  />
                }
              />
            </tr>

            {/* 관련문서 */}
            <tr>
              <TableCell
                label="관련문서"
                colSpan={3}
                editing
                input={
                  <input
                    name="related_document"
                    value={safeForm.related_document}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="관련 문서명 입력"
                  />
                }
              />
            </tr>

            {/* 관련도면/시방서 */}
            <tr>
              <TableCell
                label="관련도면/시방서"
                colSpan={3}
                editing
                input={
                  <div className="flex items-center gap-2">
                    <input
                      disabled
                      className="flex-1 border rounded px-2 py-1 bg-gray-100"
                      value={safeForm.relatedDrawing?.name || ""}
                    />
                    <button
                      type="button"
                      className="btn-basic px-3 whitespace-nowrap"
                      onClick={() => setOpenPicker(true)}
                    >
                      📂 선택
                    </button>
                    {safeForm.relatedDrawing && (
                      <button
                        type="button"
                        className="text-red-500 text-xs"
                        onClick={() => updateForm("relatedDrawing", null)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                }
              />
            </tr>

            {/* 공종 / 종류 */}
            <tr>
              <TableCell
                label="공종"
                editing
                input={
                  <select
                    name="division"
                    value={safeForm.division}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                  >
                    <option value="">선택</option>
                    {divisionOptions.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                }
              />
              <TableCell
                label="종류"
                editing
                input={
                  <input
                    disabled
                    value="자재"
                    className="w-full border rounded px-2 py-1 bg-gray-100"
                  />
                }
              />
            </tr>

            {/* 지역 / 층 */}
            <tr>
              <TableCell
                label="지역"
                editing
                input={
                  <input
                    name="area"
                    value={safeForm.area}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="지역"
                  />
                }
              />
              <TableCell
                label="층"
                editing
                input={
                  <input
                    name="level"
                    value={safeForm.level}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="층"
                  />
                }
              />
            </tr>

            {/* 좌표 */}
            <tr>
              <TableCell
                label="좌표"
                colSpan={3}
                editing
                input={
                  <input
                    name="grid"
                    value={safeForm.grid}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="좌표"
                  />
                }
              />
            </tr>

            {/* 승인요청일 / 시행일자 */}
            <tr>
              <TableCell
                label="승인요청일"
                editing
                input={
                  <input
                    type="date"
                    name="request_date"
                    value={safeForm.request_date}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                  />
                }
              />
              <TableCell
                label="시행일자"
                editing
                input={
                  <input
                    type="date"
                    name="action_date"
                    value={safeForm.action_date}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                  />
                }
              />
            </tr>

            {/* 내용 */}
            <tr>
              <TableCell
                label="내용"
                colSpan={3}
                editing
                input={
                  <textarea
                    name="description"
                    value={safeForm.description}
                    onChange={handleChange}
                    className="w-full min-h-[100px] border rounded px-2 py-1"
                  />
                }
              />
            </tr>
          </tbody>
        </table>

        {openPicker && (
          <DesignTreePicker
            open={openPicker}
            onClose={() => setOpenPicker(false)}
            onConfirm={(file) =>
              updateForm("relatedDrawing", {
                id: file.id,
                name: file.name || file.title,
                url: file.file_url || file.url || "",
              })
            }
          />
        )}
      </div>
    </FormContentManager>
  );
}
